import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  AlertTriangle, CheckCircle, Clock, Calendar, Phone, Download, Upload,
  RefreshCw, ChevronRight, ChevronLeft, ChevronsLeft, ChevronsRight,
  ChevronDown, ChevronUp, Search, FileText, X, Zap, RotateCcw, Ban,
  Timer, ArrowRight, Flame, CheckCircle2, XCircle, Info, Filter, User
} from 'lucide-react';
import { formatCurrency, formatDate } from '../../utils/helpers';
import { Skeleton, Dropdown, DropdownTrigger, DropdownPopover, DropdownMenu, DropdownItem, DatePicker } from '../ui';
import ContractDetailsModal from '../modals/ContractDetailsModal';
import CsvSetupModal from '../modals/CsvSetupModal';
import CsvImportModal from '../modals/CsvImportModal';
import { useLanguage } from '../../contexts/LanguageContext';
import { useContracts, useCsvUpload } from '../../hooks';
import { useSearchParams } from 'react-router-dom';
import * as XLSX from 'xlsx';

// ─────────────────────────────────────────────────────────
//  Helpers
// ─────────────────────────────────────────────────────────
const getDaysLeft = (expiryDate) => {
  if (!expiryDate) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const expiry = new Date(expiryDate); expiry.setHours(0, 0, 0, 0);
  return Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
};

const getExpiryStatus = (daysLeft) => {
  if (daysLeft === null) return 'no-expiry';
  if (daysLeft < 0)   return 'expired';
  if (daysLeft <= 7)  return 'critical';
  if (daysLeft <= 30) return 'alert';
  if (daysLeft <= 45) return 'warning';
  if (daysLeft <= 90) return 'caution';
  return 'healthy';
};

const EXPIRY_CFG = {
  expired:    { label: 'Expired',      bg: 'bg-red-100',     text: 'text-red-700',     border: 'border-red-200',    bar: 'bg-red-500',    Icon: XCircle },
  critical:   { label: 'Within 7 Days',    bg: 'bg-orange-100',  text: 'text-orange-700',  border: 'border-orange-200', bar: 'bg-orange-500', Icon: Flame },
  alert:      { label: 'Within 30 Days',   bg: 'bg-amber-50',    text: 'text-amber-600',   border: 'border-amber-200',  bar: 'bg-amber-400',  Icon: AlertTriangle },
  warning:    { label: 'Within 45 Days',   bg: 'bg-amber-100',   text: 'text-amber-700',   border: 'border-amber-200',  bar: 'bg-amber-500',  Icon: AlertTriangle },
  caution:    { label: 'Within 90 Days',   bg: 'bg-yellow-100',  text: 'text-yellow-700',  border: 'border-yellow-200', bar: 'bg-yellow-400', Icon: Timer },
  healthy:    { label: 'Over 90 Days',   bg: 'bg-emerald-100', text: 'text-emerald-700', border: 'border-emerald-200',bar: 'bg-emerald-500',Icon: CheckCircle2 },
  'no-expiry':{ label: 'No Date Set', bg: 'bg-gray-100',    text: 'text-gray-500',    border: 'border-gray-200',   bar: 'bg-gray-300',   Icon: Info },
};

const CONTRACT_STATUS_CFG = {
  Active:     { bg: 'bg-emerald-100', text: 'text-emerald-700' },
  Terminated: { bg: 'bg-red-100',     text: 'text-red-700'     },
  Expired:    { bg: 'bg-gray-100',    text: 'text-gray-600'    },
  Completed:  { bg: 'bg-blue-100',    text: 'text-blue-700'    },
};

// ─────────────────────────────────────────────────────────
//  Days Left Badge  — same style as PrequalificationsView stage badges
// ─────────────────────────────────────────────────────────
const DaysLeftBadge = ({ daysLeft }) => {
  const { t } = useLanguage();
  const status = getExpiryStatus(daysLeft);
  const { bg, text, Icon } = EXPIRY_CFG[status];
  if (daysLeft === null) return <span className="text-gray-400 text-xs">—</span>;
  const label = daysLeft < 0 ? `${Math.abs(daysLeft)}${t('daysOverdue') || 'd overdue'}` : daysLeft === 0 ? (t('today') || 'Today!') : `${daysLeft}${t('daysLeftText') || 'd left'}`;
  return (
    <span className={`px-2 py-1 rounded text-xs font-bold inline-block whitespace-nowrap ${bg} ${text}`}>
      {label}
    </span>
  );
};

// ─────────────────────────────────────────────────────────
//  Extend Modal
// ─────────────────────────────────────────────────────────
const ExtendModal = ({ contract, onClose, onConfirm, loading }) => {
  const minDate = (() => { 
    let d = new Date(); 
    if (contract?.expiryDate) {
      const expiry = new Date(contract.expiryDate);
      if (!isNaN(expiry.getTime())) {
        d = new Date(expiry);
      }
    }
    d.setDate(d.getDate() + 1); 
    return d.toISOString().split('T')[0]; 
  })();
  const [newEndDate, setNewEndDate] = useState('');
  const [reason, setReason] = useState('');

  const getExtendedDuration = () => {
    if (!newEndDate) return null;
    const end = new Date(newEndDate);
    if (isNaN(end.getTime())) return null;

    const compute = (d1, d2) => {
      const diffMs = d2 - d1;
      if (diffMs <= 0) return null;
      const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
      const months = Math.floor(diffDays / 30);
      const years = Math.floor(months / 12);
      if (years > 0) {
        const remMo = months % 12;
        return remMo > 0 ? `${years} yr ${remMo} mo` : `${years} yr`;
      }
      if (months > 0) {
        const remDays = diffDays % 30;
        return remDays > 0 ? `${months} mo ${remDays} d` : `${months} mo`;
      }
      return `${diffDays} days`;
    };

    let extension = null;
    let total = null;

    if (contract.expiryDate) {
      const expiry = new Date(contract.expiryDate);
      if (!isNaN(expiry.getTime())) extension = compute(expiry, end);
    }
    if (contract.startDate) {
      const start = new Date(contract.startDate);
      if (!isNaN(start.getTime())) total = compute(start, end);
    }

    return { extension, total };
  };

  const computedDurations = getExtendedDuration();

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/30 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md border border-gray-200">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="bg-blue-50 p-2 rounded-lg border border-blue-100">
              <RotateCcw className="w-4 h-4 text-blue-600" />
            </div>
            <div>
              <p className="font-semibold text-gray-900">Extend Contract</p>
              <p className="text-xs text-gray-500 truncate max-w-[200px]">{contract.clientName}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {contract.expiryDate && (
            <div className="flex items-center gap-2 p-3 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg text-sm">
              <div className="bg-white/60 p-1.5 rounded-lg border border-amber-100">
                <Calendar className="w-3.5 h-3.5 text-amber-600" />
              </div>
              <span className="text-amber-800 text-sm">
                Current expiry: <strong>{formatDate(contract.expiryDate, { day: 'numeric', month: 'short', year: 'numeric' })}</strong>
              </span>
              <DaysLeftBadge daysLeft={getDaysLeft(contract.expiryDate)} />
            </div>
          )}

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">
              New End Date <span className="text-red-500">*</span>
            </label>
            <DatePicker value={newEndDate} onChange={(e) => setNewEndDate(e.target.value)} fullWidth min={minDate} />
            
            {newEndDate && computedDurations && (computedDurations.extension || computedDurations.total) && (
              <div className="mt-3 text-sm text-gray-700 bg-blue-50 p-3 rounded-lg border border-blue-100 flex flex-col gap-1">
                {computedDurations.extension && (
                  <p><strong className="text-blue-700">Extended by:</strong> {computedDurations.extension}</p>
                )}
                {computedDurations.total && (
                  <p><strong className="text-blue-700">New Total Duration:</strong> {computedDurations.total}</p>
                )}
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">
              Reason <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Client requested 6-month extension..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none bg-slate-50 outline-none"
            />
          </div>
        </div>

        <div className="flex gap-2 px-5 pb-5">
          <button
            onClick={onClose}
            className="flex-1 h-[42px] border border-gray-200 text-gray-600 rounded-lg text-sm font-semibold hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(newEndDate, reason)}
            disabled={!newEndDate || loading}
            className="flex-1 h-[42px] bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 shadow-sm"
          >
            {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
            Extend Contract
          </button>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────
//  Terminate Modal
// ─────────────────────────────────────────────────────────
const TerminateModal = ({ contract, onClose, onConfirm, loading }) => {
  const { t } = useLanguage();
  const [reason, setReason] = useState('');
  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/30 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md border border-gray-200">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="bg-red-50 p-2 rounded-lg border border-red-100">
              <Ban className="w-4 h-4 text-red-600" />
            </div>
            <div>
              <p className="font-semibold text-gray-900">Terminate Contract</p>
              <p className="text-xs text-gray-500 truncate max-w-[200px]">{contract.clientName}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="p-3 bg-gradient-to-r from-red-50 to-orange-50 border border-red-200 rounded-lg text-sm text-red-800">
            <div className="flex items-start gap-2">
              <div className="bg-white/60 p-1.5 rounded-lg border border-red-100 shrink-0 mt-0.5">
                <AlertTriangle className="w-3.5 h-3.5 text-red-600" />
              </div>
              <div>
                <strong className="font-semibold text-red-700">This action cannot be undone.</strong>
                <p className="text-red-700 mt-0.5">Contract status will be set to <strong>{t('terminated') || 'Terminated'}</strong> and all pending expiry notifications will be cleared.</p>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">
              Reason <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Project cancelled, client withdrew..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none bg-slate-50 outline-none"
            />
          </div>
        </div>

        <div className="flex gap-2 px-5 pb-5">
          <button
            onClick={onClose}
            className="flex-1 h-[42px] border border-gray-200 text-gray-600 rounded-lg text-sm font-semibold hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(reason)}
            disabled={loading}
            className="flex-1 h-[42px] bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2 shadow-sm"
          >
            {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Ban className="w-4 h-4" />}
            Terminate
          </button>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────
//  Toast
// ─────────────────────────────────────────────────────────
const Toast = ({ toast, onDismiss }) => {
  useEffect(() => { const t = setTimeout(onDismiss, 3500); return () => clearTimeout(t); }, [onDismiss]);
  if (!toast) return null;
  const ok = toast.type === 'success';
  return (
    <div className={`fixed bottom-6 right-6 z-[9999] flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg border text-sm font-medium ${ok ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-red-50 border-red-200 text-red-800'}`}>
      {ok ? <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" /> : <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />}
      {toast.message}
      <button onClick={onDismiss} className="ml-1 opacity-50 hover:opacity-100"><X className="w-3.5 h-3.5" /></button>
    </div>
  );
};

// ─────────────────────────────────────────────────────────
//  Main Component
// ─────────────────────────────────────────────────────────
export default function ContractsView({ activeUser, isLoading, onSyncPayments }) {
  const { t } = useLanguage();
  const { contracts, loading, fetchContracts, extendContract, terminateContract } = useContracts({ enabled: true });
  const [searchParams, setSearchParams] = useSearchParams();

  const [globalFilter, setGlobalFilter] = useState('');
  const [urgencyFilter, setUrgencyFilter] = useState(() => searchParams.get('urgency') || 'all');
  const [statusFilter, setStatusFilter] = useState(() => searchParams.get('status') || 'all');
  const [assigneeFilter, setAssigneeFilter] = useState(() => searchParams.get('assignee') || 'all');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [actionLoading, setActionLoading] = useState(false);
  const [detailsModal, setDetailsModal] = useState(null);
  const [extendModal, setExtendModal] = useState(null);
  const [terminateModal, setTerminateModal] = useState(null);
  const [toast, setToast] = useState(null);
  const [showImportSetup, setShowImportSetup] = useState(false);

  const { importProgress, closeImportModal, handleFileUpload } = useCsvUpload('contracts', () => {
    fetchContracts();
  });

  useEffect(() => {
    const contractIdParam = searchParams.get('contractId');
    if (contractIdParam && contracts && contracts.length > 0) {
      const contractId = parseInt(contractIdParam, 10);
      const contractToOpen = contracts.find(c => c.id === contractId);
      if (contractToOpen) {
        setDetailsModal(contractToOpen);
        setSearchParams(prev => {
          const newParams = new URLSearchParams(prev);
          newParams.delete('contractId');
          return newParams;
        });
      }
    }
  }, [searchParams, contracts, setSearchParams]);

  useEffect(() => {
    const statusParam = searchParams.get('status');
    if (statusParam && statusParam !== statusFilter) {
      setStatusFilter(statusParam);
    }
    const urgencyParam = searchParams.get('urgency');
    if (urgencyParam && urgencyParam !== urgencyFilter) {
      setUrgencyFilter(urgencyParam);
    }
    const assigneeParam = searchParams.get('assignee');
    if (assigneeParam && assigneeParam !== assigneeFilter) {
      setAssigneeFilter(assigneeParam);
    }
  }, [searchParams]);

  const displayLoading = loading || isLoading;

  // ── Enrich contracts
  const enriched = useMemo(() =>
    (contracts || []).map(c => {
      const daysLeft = getDaysLeft(c.expiryDate);
      return { ...c, daysLeft, expiryStatus: getExpiryStatus(daysLeft) };
    }), [contracts]);

  // ── Stats
  const stats = useMemo(() => ({
    total:      enriched.length,
    expired:    enriched.filter(c => c.expiryStatus === 'expired').length,
    critical:   enriched.filter(c => c.expiryStatus === 'critical').length,
    warning:    enriched.filter(c => c.expiryStatus === 'warning').length,
    caution:    enriched.filter(c => c.expiryStatus === 'caution').length,
    healthy:    enriched.filter(c => c.expiryStatus === 'healthy').length,
  }), [enriched]);

  const uniqueAssignees = useMemo(() => {
    const assignees = enriched.map(c => c.tender?.assignee?.username || c.assignee?.username).filter(Boolean);
    return [...new Set(assignees)].sort();
  }, [enriched]);

  // ── Filter + sort
  const filtered = useMemo(() => {
    let result = enriched;
    if (globalFilter) {
      const q = globalFilter.toLowerCase();
      result = result.filter(c =>
        c.clientName?.toLowerCase().includes(q) ||
        c.phone?.toLowerCase().includes(q) ||
        c.profile?.mobile?.toLowerCase().includes(q) ||
        String(c.orderNo).toLowerCase().includes(q) ||
        c.projectName?.toLowerCase().includes(q) ||
        c.profile?.email?.toLowerCase().includes(q) ||
        c.serviceType?.toLowerCase().includes(q)
      );
    }
    if (urgencyFilter !== 'all' && urgencyFilter !== 'custom') {
      if (urgencyFilter === 'warning') {
        result = result.filter(c => c.daysLeft !== null && c.daysLeft >= 0 && c.daysLeft <= 45);
      } else if (urgencyFilter === 'alert') {
        result = result.filter(c => c.daysLeft !== null && c.daysLeft >= 0 && c.daysLeft <= 30);
      } else if (urgencyFilter === 'caution') {
        result = result.filter(c => c.daysLeft !== null && c.daysLeft >= 0 && c.daysLeft <= 90);
      } else if (urgencyFilter === 'critical') {
        result = result.filter(c => c.daysLeft !== null && c.daysLeft >= 0 && c.daysLeft <= 7);
      } else {
        result = result.filter(c => c.expiryStatus === urgencyFilter);
      }
    }
    if (urgencyFilter === 'custom') {
      if (customFrom) {
        const fromDate = new Date(customFrom);
        result = result.filter(c => c.expiryDate && new Date(c.expiryDate) >= fromDate);
      }
      if (customTo) {
        const toDate = new Date(customTo);
        toDate.setHours(23, 59, 59, 999);
        result = result.filter(c => c.expiryDate && new Date(c.expiryDate) <= toDate);
      }
    }
    if (statusFilter  !== 'all') result = result.filter(c => c.status === statusFilter);
    if (assigneeFilter !== 'all') result = result.filter(c => (c.tender?.assignee?.username || c.assignee?.username) === assigneeFilter);
    return result.sort((a, b) => {
      if (a.daysLeft === null && b.daysLeft === null) return 0;
      if (a.daysLeft === null) return 1;
      if (b.daysLeft === null) return -1;
      return a.daysLeft - b.daysLeft;
    });
  }, [enriched, globalFilter, urgencyFilter, statusFilter, assigneeFilter, customFrom, customTo]);

  useEffect(() => { setPageIndex(0); }, [globalFilter, urgencyFilter, statusFilter, assigneeFilter, customFrom, customTo]);

  const paginated = useMemo(() => filtered.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize), [filtered, pageIndex, pageSize]);
  const totalPages = Math.ceil(filtered.length / pageSize);

  const showToast = useCallback((message, type = 'success') => setToast({ message, type }), []);

  const handleSync = async () => {
    setSyncing(true);
    await fetchContracts();
    if (onSyncPayments) onSyncPayments();
    setSyncing(false);
  };

  const handleExport = async () => {
    try {
      setSyncing(true);
      const headers = ['Contract #', 'Client', 'Phone', 'Project', 'Assignee', 'Start Date', 'End Date', 'Days Left', 'Service Type', 'Total Value', 'Collected', 'Status'];
      const rows = filtered.map(c => [c.orderNo, c.clientName, c.phone, c.projectName || '-', c.tender?.assignee?.username || c.assignee?.username || '-', c.startDate || '-', c.expiryDate || '-', c.daysLeft ?? 'N/A', c.serviceType || '-', c.totalValue, c.totalPaid, c.status || '-']);
      
      const aoa = [headers, ...rows];
      const ws = XLSX.utils.aoa_to_sheet(aoa);
      ws['!cols'] = [12, 22, 15, 20, 15, 12, 12, 10, 15, 12, 12, 10].map(w => ({ wch: w }));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Contracts');
      XLSX.writeFile(wb, `contracts_export_${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (e) { console.error(e); } finally { setSyncing(false); }
  };

  const handleExtendConfirm = async (newEndDate, reason) => {
    if (!extendModal || !newEndDate) return;
    setActionLoading(true);
    try { 
      const updated = await extendContract(extendModal.id, newEndDate, reason); 
      showToast('Contract extended successfully!'); 
      setExtendModal(null); 
      setDetailsModal(updated);
    }
    catch (e) { showToast(e.message || 'Failed to extend contract', 'error'); }
    finally { setActionLoading(false); }
  };

  const handleTerminateConfirm = async (reason) => {
    if (!terminateModal) return;
    setActionLoading(true);
    try { 
      const updated = await terminateContract(terminateModal.id, reason); 
      showToast('Contract terminated.'); 
      setTerminateModal(null); 
      setDetailsModal(updated);
    }
    catch (e) { showToast(e.message || 'Failed to terminate contract', 'error'); }
    finally { setActionLoading(false); }
  };

  const criticalContracts = useMemo(() =>
    enriched.filter(c => (c.expiryStatus === 'critical' || c.expiryStatus === 'expired') && c.status === 'Active'),
    [enriched]);

  const activeContractsCount = enriched.filter(c => c.status === 'Active').length;
  const totalContractValue = enriched.reduce((sum, c) => sum + (c.status === 'Active' ? (Number(c.totalValue) || 0) : 0), 0);

  // ─────────────────────────────────────────────────────
  //  JSX
  // ─────────────────────────────────────────────────────
  return (
    <div className="space-y-4 animate-fade-in">

      {/* ── Page Heading — same as PrequalificationsView ── */}
      <div className="mb-2">
        <h2 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-indigo-600">
          {t('allContract') || 'All Contract'}
        </h2>
        <p className="text-gray-500 mt-1">{enriched.length} {t('contracts') || 'contracts'}</p>
      </div>

      {/* ── Stats Cards ── */}
      {!displayLoading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <div className="bg-white p-4 rounded-2xl border border-gray-200 shadow-sm transition-all hover:shadow-md hover:border-blue-200">
            <div className="flex justify-between items-start mb-4">
              <div className="p-2 bg-blue-100 text-blue-600 rounded-xl">
                <FileText className="w-5 h-5" />
              </div>
            </div>
            <div>
              <p className="text-xs text-gray-500 font-medium truncate">{t('totalContracts') || 'Total Contracts'}</p>
              <h3 className="text-lg font-bold text-gray-900 mt-1 truncate">{enriched.length}</h3>
            </div>
          </div>
          
          <div className="bg-white p-4 rounded-2xl border border-gray-200 shadow-sm transition-all hover:shadow-md hover:border-emerald-200">
            <div className="flex justify-between items-start mb-4">
              <div className="p-2 bg-emerald-100 text-emerald-600 rounded-xl">
                <CheckCircle2 className="w-5 h-5" />
              </div>
            </div>
            <div>
              <p className="text-xs text-gray-500 font-medium truncate">{t('activeContracts') || 'Active Contracts'}</p>
              <h3 className="text-lg font-bold text-gray-900 mt-1 truncate">{activeContractsCount}</h3>
            </div>
          </div>

          <div className="bg-white p-4 rounded-2xl border border-gray-200 shadow-sm transition-all hover:shadow-md hover:border-amber-200">
            <div className="flex justify-between items-start mb-4">
              <div className="p-2 bg-amber-100 text-amber-600 rounded-xl">
                <Timer className="w-5 h-5" />
              </div>
            </div>
            <div>
              <p className="text-xs text-gray-500 font-medium truncate">{t('needsAttention') || 'Needs Attention'}</p>
              <h3 className="text-lg font-bold text-gray-900 mt-1 truncate">{criticalContracts.length}</h3>
            </div>
          </div>

          <div className="bg-white p-4 rounded-2xl border border-gray-200 shadow-sm transition-all hover:shadow-md hover:border-indigo-200">
            <div className="flex justify-between items-start mb-4">
              <div className="p-2 bg-indigo-100 text-indigo-600 rounded-xl">
                <Zap className="w-5 h-5" />
              </div>
            </div>
            <div>
              <p className="text-xs text-gray-500 font-medium truncate">{t('activeValue') || 'Active Value'}</p>
              <h3 className="text-lg font-bold text-gray-900 mt-1 truncate">{formatCurrency(totalContractValue)}</h3>
            </div>
          </div>
        </div>
      )}

      {/* ── Critical Alert Banner — same amber pattern as PrequalificationsView warning ── */}
      {!displayLoading && criticalContracts.length > 0 && (
        <div className="bg-gradient-to-r from-red-50 to-orange-50 border border-red-200 p-3 md:p-4 rounded-xl flex flex-col sm:flex-row items-start sm:items-center gap-3 shadow-sm transition-all hover:shadow-md">
          <div className="bg-white/60 p-2 rounded-lg text-red-600 shrink-0 shadow-sm border border-red-100">
            <Flame className="w-5 h-5" />
          </div>
          <div className="text-sm text-red-900 flex-1 leading-relaxed">
            <strong className="font-semibold text-red-700 mr-1.5">{t('actionRequired') || 'Action Required:'}</strong>
            <span className="text-red-800/90">
              {criticalContracts.length} {t('activeContractsNeedAttention') || 'active contract(s) need attention'} —
              {criticalContracts.filter(c => c.expiryStatus === 'expired').length > 0 &&
                ` ${criticalContracts.filter(c => c.expiryStatus === 'expired').length} ${t('alreadyExpired') || 'already expired'},`}
              {criticalContracts.filter(c => c.expiryStatus === 'critical').length > 0 &&
                ` ${criticalContracts.filter(c => c.expiryStatus === 'critical').length} ${t('expiringWithin') || 'expiring within'} 7 days.`}
              {' '}{t('pleaseExtendOrTerminate') || 'Please extend or terminate.'}
            </span>
          </div>
        </div>
      )}

      {/* ── Header & Toolbar — same exact structure as PrequalificationsView ── */}
      <div className="bg-white p-4 md:p-5 rounded-xl border border-gray-200 shadow-sm flex flex-col gap-4">

        {/* Top Row: Search & Actions */}
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">

          {/* Search — exact same classes */}
          <div className="relative w-full lg:max-w-md flex-shrink-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              className="w-full pl-10 pr-4 h-[42px] bg-slate-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
              placeholder="Search project, client, mobile, email..."
              value={globalFilter ?? ''}
              onChange={(e) => setGlobalFilter(e.target.value)}
            />
          </div>

          {/* Actions — same button pattern */}
          <div className="flex flex-wrap items-center gap-2 w-full lg:w-auto justify-between sm:justify-end">

            <button
              onClick={handleExport}
              disabled={syncing}
              className="flex-1 sm:flex-none justify-center h-[42px] bg-sky-50 text-sky-700 border border-sky-200 px-3 sm:px-4 rounded-lg text-sm font-semibold flex items-center gap-2 hover:bg-sky-100 transition-colors whitespace-nowrap shadow-sm disabled:opacity-50"
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">{t('exportCsv') || 'Export CSV'}</span>
            </button>
            <button
              onClick={() => setShowImportSetup(true)}
              className="flex-1 sm:flex-none justify-center h-[42px] bg-emerald-50 text-emerald-700 border border-emerald-200 px-3 sm:px-4 rounded-lg text-sm font-semibold flex items-center gap-2 hover:bg-emerald-100 transition-colors whitespace-nowrap shadow-sm"
            >
              <Upload className="w-4 h-4" />
              <span className="hidden sm:inline">{t('importCsv') || 'Import CSV'}</span>
            </button>
          </div>
        </div>

        {/* Bottom Row: Filters — same structure as PrequalificationsView */}
        <div className="flex flex-col xl:flex-row items-stretch xl:items-center justify-between gap-3 pt-4 border-t border-gray-100">
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1.5 text-gray-400 font-medium text-sm mr-1">
              <Filter className="w-4 h-4" />
              <span className="hidden sm:inline">{t('filters') || 'Filters:'}</span>
            </div>

            {/* Urgency filter dropdown */}
            <Dropdown className="w-full sm:w-auto">
              <DropdownTrigger>
                <button className="w-full sm:w-auto min-w-[140px] bg-white hover:bg-slate-50 border border-gray-200 rounded-lg text-sm px-3 h-[40px] focus:ring-2 focus:ring-blue-500 outline-none flex justify-between items-center text-left shadow-sm transition-colors">
                  <span className="font-medium text-gray-700">
                    {urgencyFilter === 'all' 
                      ? (t('') || 'All') 
                      : urgencyFilter === 'critical' ? (t('sevenDaysOrLess') || 'Within 7 Days')
                      : urgencyFilter === 'alert' ? (t('thirtyDaysOrLess') || 'Within 30 Days')
                      : urgencyFilter === 'warning' ? (t('fortyFiveDaysOrLess') || 'Within 45 Days')
                      : urgencyFilter === 'caution' ? (t('ninetyDaysOrLess') || 'Within 90 Days')
                      : urgencyFilter === 'healthy' ? (t('moreThanNinetyDays') || 'Over 90 Days')
                      : urgencyFilter === 'custom' ? (t('customDate') || 'Custom')
                      : EXPIRY_CFG[urgencyFilter]?.label}
                  </span>
                  <ChevronDown className="w-4 h-4 text-gray-400 shrink-0 ml-2" />
                </button>
              </DropdownTrigger>
              <DropdownPopover className="w-[180px]">
                <DropdownMenu
                  selectedKeys={new Set([urgencyFilter])}
                  selectionMode="single"
                  onSelectionChange={(keys) => { setUrgencyFilter(Array.from(keys)[0]); }}
                >
                  <DropdownItem id="all">{t('allUrgency') || 'All Urgency'}</DropdownItem>
                  <DropdownItem id="critical">{t('sevenDaysOrLess') || 'Within 7 Days'}</DropdownItem>
                  <DropdownItem id="alert">{t('thirtyDaysOrLess') || 'Within 30 Days'}</DropdownItem>
                  <DropdownItem id="warning">{t('fortyFiveDaysOrLess') || 'Within 45 Days'}</DropdownItem>
                  <DropdownItem id="caution">{t('ninetyDaysOrLess') || 'Within 90 Days'}</DropdownItem>
                  <DropdownItem id="healthy">{t('moreThanNinetyDays') || 'Over 90 Days'}</DropdownItem>
                  <DropdownItem id="custom">{t('customDate') || 'Custom'}</DropdownItem>
                </DropdownMenu>
              </DropdownPopover>
            </Dropdown>

            {/* Status filter dropdown */}
            <Dropdown className="w-full sm:w-auto">
              <DropdownTrigger>
                <button className="w-full sm:w-auto min-w-[140px] bg-white hover:bg-slate-50 border border-gray-200 rounded-lg text-sm px-3 h-[40px] focus:ring-2 focus:ring-blue-500 outline-none flex justify-between items-center text-left shadow-sm transition-colors">
                  <span className="font-medium text-gray-700">
                    {statusFilter === 'all' 
                      ? (t('allStatus') || 'All Status')
                      : statusFilter === 'Active' ? (t('active') || 'Active')
                      : statusFilter === 'Terminated' ? (t('terminated') || 'Terminated')
                      : statusFilter === 'Expired' ? (t('expired') || 'Expired')
                      : statusFilter === 'Completed' ? (t('completed') || 'Completed')
                      : statusFilter}
                  </span>
                  <ChevronDown className="w-4 h-4 text-gray-400 shrink-0 ml-2" />
                </button>
              </DropdownTrigger>
              <DropdownPopover className="w-[160px]">
                <DropdownMenu
                  selectedKeys={new Set([statusFilter])}
                  selectionMode="single"
                  onSelectionChange={(keys) => setStatusFilter(Array.from(keys)[0])}
                >
                  <DropdownItem id="all">{t('allStatus') || 'All Status'}</DropdownItem>
                  <DropdownItem id="Active">{t('active') || 'Active'}</DropdownItem>
                  <DropdownItem id="Terminated">{t('terminated') || 'Terminated'}</DropdownItem>
                  <DropdownItem id="Expired">{t('expired') || 'Expired'}</DropdownItem>
                  <DropdownItem id="Completed">{t('completed') || 'Completed'}</DropdownItem>
                </DropdownMenu>
              </DropdownPopover>
            </Dropdown>

            {/* Assignee filter dropdown */}
            <Dropdown className="w-full sm:w-auto">
              <DropdownTrigger>
                <button className="w-full sm:w-auto min-w-[140px] bg-white hover:bg-slate-50 border border-gray-200 rounded-lg text-sm px-3 h-[40px] focus:ring-2 focus:ring-blue-500 outline-none flex justify-between items-center text-left shadow-sm transition-colors">
                  <span className="font-medium text-gray-700">
                    {assigneeFilter === 'all' 
                      ? (t('allAssignees') || 'All Assignees')
                      : assigneeFilter}
                  </span>
                  <ChevronDown className="w-4 h-4 text-gray-400 shrink-0 ml-2" />
                </button>
              </DropdownTrigger>
              <DropdownPopover className="w-[160px]">
                <DropdownMenu
                  selectedKeys={new Set([assigneeFilter])}
                  selectionMode="single"
                  onSelectionChange={(keys) => setAssigneeFilter(Array.from(keys)[0])}
                >
                  <DropdownItem id="all">{t('allAssignees') || 'All Assignees'}</DropdownItem>
                  {uniqueAssignees.map(a => (
                    <DropdownItem key={a} id={a}>{a}</DropdownItem>
                  ))}
                </DropdownMenu>
              </DropdownPopover>
            </Dropdown>

            {urgencyFilter === 'custom' && (
              <div className="flex items-center gap-2">
                <div className="w-[130px]">
                  <DatePicker
                    value={customFrom}
                    onChange={(e) => setCustomFrom(e.target.value)}
                    size="sm"
                  />
                </div>
                <span className="text-gray-400">-</span>
                <div className="w-[130px]">
                  <DatePicker
                    value={customTo}
                    onChange={(e) => setCustomTo(e.target.value)}
                    size="sm"
                  />
                </div>
              </div>
            )}

            {/* Clear filters */}
            {(globalFilter || urgencyFilter !== 'all' || statusFilter !== 'all' || assigneeFilter !== 'all') && (
              <button
                onClick={() => { setGlobalFilter(''); setUrgencyFilter('all'); setStatusFilter('all'); setAssigneeFilter('all'); setCustomFrom(''); setCustomTo(''); }}
                className="text-xs font-semibold text-red-500 px-3 h-[40px] hover:bg-red-50 rounded-lg border border-transparent hover:border-red-200 transition-colors flex-shrink-0"
              >
                {t('clear') || 'Clear'}
              </button>
            )}
          </div>

          {/* Right: stats summary removed as per user request */}
        </div>
      </div>

      {/* ── Mobile Card View — same as PrequalificationsView ── */}
      <div className={`md:hidden space-y-3 ${displayLoading ? 'opacity-70 pointer-events-none' : ''}`}>
        {displayLoading && contracts.length === 0 ? (
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm space-y-3">
                <div className="flex justify-between items-start">
                  <div className="space-y-2">
                    <Skeleton width={140} height={18} animation="wave" />
                    <Skeleton width={110} height={14} animation="wave" />
                  </div>
                  <Skeleton variant="rounded" width={70} height={22} animation="wave" />
                </div>
                <div className="pt-3 border-t flex justify-between">
                  <Skeleton width={90} height={14} animation="wave" />
                  <Skeleton width={70} height={14} animation="wave" />
                </div>
              </div>
            ))}
          </div>
        ) : paginated.length === 0 ? (
          <div className="bg-white p-8 rounded-xl border border-gray-200 text-center text-gray-500">
            No contracts found.
          </div>
        ) : (
          paginated.map(contract => {
            const cfg = EXPIRY_CFG[contract.expiryStatus] || EXPIRY_CFG.healthy;
            const isTerminated = contract.status === 'Terminated';
            return (
              <div
                key={contract.id}
                onClick={() => setDetailsModal(contract)}
                className={`cursor-pointer bg-white rounded-xl border border-gray-200/90 shadow-sm hover:shadow-md transition-all overflow-hidden border-l-4 ${cfg.bar.replace('bg-', 'border-l-')}`}
              >
                <div className="p-4 space-y-3">
                  {/* Top row */}
                  <div className="flex justify-between items-start gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="font-bold text-gray-900 text-base leading-snug truncate">{contract.projectName || contract.clientName}</p>
                      {contract.projectName && (
                        <p className="text-xs text-gray-500 font-medium flex items-center gap-1 truncate mt-0.5">
                          <User className="w-3.5 h-3.5" /> {contract.clientName}
                        </p>
                      )}
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        {contract.phone && (
                          <span className="text-xs text-gray-600 font-mono flex items-center gap-1">
                            <Phone className="w-3.5 h-3.5 text-gray-400 shrink-0" /> {contract.phone}
                          </span>
                        )}
                        {contract.serviceType && (
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-indigo-50 text-indigo-700 border border-indigo-100">
                            {contract.serviceType}
                          </span>
                        )}
                        {(contract.tender?.assignee || contract.assignee) && (
                          <span className="text-xs text-gray-600 font-medium flex items-center gap-1">
                            <User className="w-3.5 h-3.5 text-gray-400 shrink-0" /> {contract.tender?.assignee?.username || contract.assignee?.username}
                          </span>
                        )}
                      </div>
                    </div>
                    <DaysLeftBadge daysLeft={contract.daysLeft} />
                  </div>

                  {/* Financials */}
                  <div className="bg-slate-50 border border-slate-100 rounded-lg p-2.5 flex items-center justify-between text-xs">
                    <span className="text-gray-400 font-medium">Total Value</span>
                    <span className="text-sm font-bold text-emerald-600">{formatCurrency(contract.totalValue)}</span>
                  </div>

                  {/* Footer */}
                  <div className="flex justify-between items-center pt-1 text-xs text-gray-500">
                    <span className="flex items-center gap-1.5 text-gray-600 font-medium">
                      <Calendar className="w-3.5 h-3.5 text-gray-400" />
                      {contract.expiryDate
                        ? `Expires: ${formatDate(contract.expiryDate, { day: 'numeric', month: 'short', year: 'numeric' })}`
                        : 'No expiry set'}
                    </span>
                    {!isTerminated ? (
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={(e) => { e.stopPropagation(); setExtendModal(contract); }}
                          className="flex items-center gap-1 px-2.5 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-semibold hover:bg-blue-700 transition-colors shadow-sm"
                        >
                          <RotateCcw className="w-3 h-3" /> Extend
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); setTerminateModal(contract); }}
                          className="p-1.5 bg-red-50 border border-red-200 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
                        >
                          <Ban className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ) : (
                      <span className="px-2 py-1 rounded text-xs font-bold bg-red-100 text-red-700">{t('terminated') || 'Terminated'}</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* ── Desktop Table — same exact structure as PrequalificationsView ── */}
      <div className={`hidden md:block bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden ${displayLoading ? 'opacity-70 pointer-events-none' : ''}`}>
        {displayLoading && contracts.length === 0 ? (
          <div className="p-4 space-y-4">
            <div className="flex items-center gap-4 border-b pb-3">
              {[1, 2, 3, 4, 5, 6].map(i => <Skeleton key={i} width={100} height={16} animation="wave" />)}
            </div>
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="flex items-center gap-4 py-2 border-b last:border-0">
                <Skeleton width={150} height={16} animation="wave" />
                <Skeleton width={100} height={14} animation="wave" />
                <Skeleton variant="rounded" width={80} height={24} animation="wave" />
                <Skeleton width={80} height={14} animation="wave" />
                <Skeleton width={80} height={14} animation="wave" />
              </div>
            ))}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              {/* thead — exact same as PrequalificationsView: bg-slate-50, border-b, uppercase tracking-wider */}
              <thead className="bg-slate-50 border-b border-gray-200">
                <tr>
                  {[
                    { label: t('clientContractTh') || 'Client / Contract', cls: 'w-[220px]' },
                    { label: t('contractNoTh') || 'Contract #',         cls: 'hidden xl:table-cell' },
                    { label: t('assignee') || 'Assignee',             cls: 'hidden lg:table-cell' },
                    { label: t('startDateTh') || 'Start Date',          cls: 'hidden lg:table-cell' },
                    { label: t('expiryDateTh') || 'Expiry Date',         cls: '' },
                    { label: t('daysLeftTh') || 'Days Left',           cls: 'text-center' },
                    { label: t('statusTh') || 'Status',              cls: 'hidden lg:table-cell' },
                    { label: t('valueTh') || 'Value',               cls: 'text-right hidden xl:table-cell' },
                  ].map(({ label, cls }, idx) => (
                    <th key={label} className={`px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider ${cls}`}>
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>

              {/* tbody — same: bg-white divide-y divide-gray-200 */}
              <tbody className="bg-white divide-y divide-gray-200">
                {paginated.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center py-14 text-gray-500">
                      <FileText className="w-10 h-10 mx-auto mb-3 opacity-20" />
                      <p>No contracts found.</p>
                    </td>
                  </tr>
                ) : (
                  paginated.map(contract => {
                    const cfg = EXPIRY_CFG[contract.expiryStatus] || EXPIRY_CFG.healthy;
                    const statusCfg = CONTRACT_STATUS_CFG[contract.status] || {};
                    const isTerminated = contract.status === 'Terminated';
                    const isUrgent = (contract.expiryStatus === 'expired' || contract.expiryStatus === 'critical') && !isTerminated;
                    return (
                      <tr
                        key={contract.id}
                        onClick={() => setDetailsModal(contract)}
                        className={`cursor-pointer transition-colors ${isUrgent ? 'bg-red-50/30 hover:bg-red-50/50' : 'hover:bg-slate-50'}`}
                      >
                        {/* Client */}
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <div className="flex items-center gap-2.5">
                            <div className={`w-1 min-h-[36px] rounded-full shrink-0 self-stretch ${cfg.bar}`} />
                            <div className="min-w-0">
                              <div className="font-bold text-gray-900 truncate max-w-[150px]">{contract.projectName || contract.clientName}</div>
                              {contract.projectName && (
                                <div className="text-xs text-gray-500 font-medium flex items-center gap-1 truncate max-w-[150px] mt-0.5">
                                  <User className="w-3 h-3" /> {contract.clientName}
                                </div>
                              )}
                              {contract.phone && (
                                <div className="text-xs text-gray-500 font-mono flex items-center gap-1 mt-0.5">
                                  <Phone className="w-3 h-3 text-gray-400" /> {contract.phone}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>

                        {/* Contract # */}
                        <td className="px-6 py-4 whitespace-nowrap text-sm hidden xl:table-cell">
                          <span className="font-mono text-xs text-gray-500">#{contract.orderNo || contract.id}</span>
                          {contract.serviceType && (
                            <div className="mt-0.5">
                              <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-indigo-50 text-indigo-700 border border-indigo-100">
                                {contract.serviceType}
                              </span>
                            </div>
                          )}
                        </td>

                        {/* Assignee */}
                        <td className="px-6 py-4 whitespace-nowrap text-sm hidden lg:table-cell">
                          {(contract.tender?.assignee || contract.assignee) ? (
                            <div className="flex items-center gap-1.5 text-gray-700">
                              <User className="w-3.5 h-3.5 text-gray-400" />
                              <span className="font-medium text-xs">{contract.tender?.assignee?.username || contract.assignee?.username}</span>
                            </div>
                          ) : (
                            <span className="text-gray-300">—</span>
                          )}
                        </td>

                        {/* Start Date */}
                        <td className="px-6 py-4 whitespace-nowrap text-sm hidden lg:table-cell">
                          {contract.startDate
                            ? <span className="text-gray-500 text-xs">{formatDate(contract.startDate, { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                            : <span className="text-gray-300">—</span>}
                        </td>

                        {/* Expiry Date */}
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {contract.expiryDate ? (
                            <div className="flex items-center gap-1.5">
                              <Calendar className="w-3.5 h-3.5 text-indigo-400 shrink-0" />
                              <span className="text-gray-700 font-medium text-xs">
                                {formatDate(contract.expiryDate, { day: 'numeric', month: 'short', year: 'numeric' })}
                              </span>
                            </div>
                          ) : <span className="text-gray-300">—</span>}
                        </td>

                        {/* Days Left */}
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                          <DaysLeftBadge daysLeft={contract.daysLeft} />
                        </td>

                        {/* Status */}
                        <td className="px-6 py-4 whitespace-nowrap text-sm hidden lg:table-cell">
                          {contract.status ? (
                            <span className={`px-2 py-1 rounded text-xs font-bold inline-block whitespace-nowrap ${statusCfg.bg || 'bg-gray-100'} ${statusCfg.text || 'text-gray-600'}`}>
                              {contract.status}
                            </span>
                          ) : '—'}
                        </td>

                        {/* Value */}
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-bold text-gray-900 hidden xl:table-cell">
                          {formatCurrency(contract.totalValue)}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Pagination — exact same as PrequalificationsView ── */}
      {filtered.length > 0 && (
        <div className="p-4 rounded-xl border border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3 bg-white sm:bg-slate-50 shadow-sm">
          {/* Page Size */}
          <div className="flex items-center gap-2 w-full sm:w-auto justify-between sm:justify-start">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">{t('show') || 'Show'}</span>
              <Dropdown>
                <DropdownTrigger>
                  <button className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 flex justify-between items-center w-[70px] shadow-sm">
                    {pageSize}
                    <ChevronDown className="w-3 h-3 text-gray-400 shrink-0 ml-1" />
                  </button>
                </DropdownTrigger>
                <DropdownPopover className="w-[70px]" placement="top">
                  <DropdownMenu
                    selectedKeys={new Set([pageSize.toString()])}
                    selectionMode="single"
                    onSelectionChange={(keys) => { setPageSize(Number(Array.from(keys)[0])); setPageIndex(0); }}
                  >
                    {[10, 20, 50].map(size => (
                      <DropdownItem key={size} id={size.toString()}>{size}</DropdownItem>
                    ))}
                  </DropdownMenu>
                </DropdownPopover>
              </Dropdown>
              <span className="text-sm text-gray-600">{t('perPage') || 'per page'}</span>
            </div>
            <div className="sm:hidden text-xs text-gray-600 font-medium">
              {t('page') || 'Page'} <span className="font-bold text-gray-900">{pageIndex + 1}</span> / <span className="font-bold text-gray-900">{totalPages}</span>
            </div>
          </div>

          {/* Desktop page info */}
          <div className="hidden sm:block text-sm text-gray-600">
            {t('page') || 'Page'}{' '}
            <span className="font-semibold text-gray-900">{pageIndex + 1}</span>{' '}
            {t('of') || 'of'}{' '}
            <span className="font-semibold text-gray-900">{totalPages}</span>
            {' · '}
            <span className="text-gray-500">{filtered.length} {t('contracts') || 'contracts'}</span>
          </div>

          {/* Nav buttons */}
          <div className="flex items-center justify-center gap-1 w-full sm:w-auto">
            <button onClick={() => setPageIndex(0)} disabled={pageIndex === 0}
              className="p-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm" title="First page">
              <ChevronsLeft className="w-4 h-4" />
            </button>
            <button onClick={() => setPageIndex(p => Math.max(0, p - 1))} disabled={pageIndex === 0}
              className="p-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm" title="Previous page">
              <ChevronLeft className="w-4 h-4" />
            </button>

            <div className="hidden sm:flex items-center gap-1 mx-2">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) pageNum = i;
                else if (pageIndex <= 2) pageNum = i;
                else if (pageIndex >= totalPages - 3) pageNum = totalPages - 5 + i;
                else pageNum = pageIndex - 2 + i;
                return (
                  <button key={pageNum} onClick={() => setPageIndex(pageNum)}
                    className={`w-9 h-9 rounded-lg text-sm font-medium transition-colors ${pageIndex === pageNum ? 'bg-blue-600 text-white' : 'border border-gray-200 hover:bg-gray-50 text-gray-700'}`}>
                    {pageNum + 1}
                  </button>
                );
              })}
            </div>

            <button onClick={() => setPageIndex(p => Math.min(totalPages - 1, p + 1))} disabled={pageIndex >= totalPages - 1}
              className="p-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm" title="Next page">
              <ChevronRight className="w-4 h-4" />
            </button>
            <button onClick={() => setPageIndex(totalPages - 1)} disabled={pageIndex >= totalPages - 1}
              className="p-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm" title="Last page">
              <ChevronsRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* ── Auto-notify text ── */}
      <div className="flex items-center justify-center gap-2 pt-6 pb-2 text-sm text-gray-500">
        <Zap className="w-4 h-4 text-amber-500 shrink-0" />
        <span className="text-center sm:text-left"><strong className="text-gray-700">{t('autoNotificationActive') || 'Auto-Notification Active:'}</strong> {t('autoNotificationDesc') || 'Daily cron runs at midnight (PKT). Contracts expiring within 45 days trigger in-app notifications automatically.'}</span>
        <div className="flex items-center gap-1.5 text-emerald-600 font-semibold text-xs ml-1 shrink-0">
          <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
          {t('running') || 'Running'}
        </div>
      </div>

      {/* ── Modals ── */}
      {detailsModal && (
        <ContractDetailsModal
          contract={detailsModal}
          onClose={() => setDetailsModal(null)}
          onExtend={() => { setExtendModal(detailsModal); }}
          onTerminate={() => { setTerminateModal(detailsModal); }}
          daysLeftBadge={<DaysLeftBadge daysLeft={detailsModal.daysLeft} />}
        />
      )}
      {extendModal && (
        <ExtendModal contract={extendModal} onClose={() => setExtendModal(null)} onConfirm={handleExtendConfirm} loading={actionLoading} />
      )}
      {terminateModal && (
        <TerminateModal contract={terminateModal} onClose={() => setTerminateModal(null)} onConfirm={handleTerminateConfirm} loading={actionLoading} />
      )}

      {/* ── CSV Modals ── */}
      <CsvSetupModal
        isOpen={showImportSetup}
        importType="contracts"
        onClose={() => setShowImportSetup(false)}
        onStartImport={(file) => {
          setShowImportSetup(false);
          handleFileUpload({ target: { files: [file], value: '' } });
        }}
      />

      {importProgress.open && (
        <CsvImportModal progress={importProgress} onClose={closeImportModal} importType="contracts" />
      )}

      {/* ── Toast ── */}
      {toast && <Toast toast={toast} onDismiss={() => setToast(null)} />}

    </div>
  );
}
